import React, {
    useState,
    useEffect,
} from 'react';

import CodeMirror from '@uiw/react-codemirror';
import { autocompletion, startCompletion, acceptCompletion } from '@codemirror/autocomplete';
import { EditorState, Prec } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { useDebounceCallback } from 'usehooks-ts';
import {
    BiExpand,
} from 'react-icons/bi';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';


const Editor = (props) => {
    const [autocompletionOpen, setAutocompletionOpen] = useState(false);
    const debouncedPropChange = useDebounceCallback(props.onChange, 350);

    function flattenObject(obj, prefix = '') {
        return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];
            const newKey = prefix ? `${prefix}.${key}` : key;

            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                return acc.concat(newKey, flattenObject(value, newKey));
            }

            return acc.concat(newKey);
        }, []);
    }

    useEffect(() => {
        debouncedPropChange(props.currentValue);
    }, [props.currentValue]);

    const handleChange = (value) => {
        props.setCurrentValue(value);
    };

    const focusNextTabableElement = (backward = false) => {
        // // list all elements with tabindex:
        const focusableElements = document.querySelectorAll(
            'a[href], button:not([tabindex="-1"]), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"]):not(.string-input-cm), .cm-content',
        );
        // focus next element:
        for (let i = 0; i < focusableElements.length; i++) {
            // if document.activeElement is a child of focusableElements[i]:
            if (focusableElements[i].contains(document.activeElement)) {
                // If there's a next focusable element, focus it
                if (backward) {
                    if (focusableElements[i - 1]) {
                        focusableElements[i - 1].focus();
                    } else {
                        focusableElements[focusableElements.length - 1].focus();
                    }
                    return true;
                }
                if (focusableElements[i + 1]) {
                    focusableElements[i + 1].focus();
                } else {
                    focusableElements[0].focus();
                }
                return true;
            }
        }

        return false;
    };

    function isInBraces(state) {
        const { from } = state.selection.main;
        const line = state.doc.lineAt(from);
        const lineText = line.text.slice(0, from - line.from);
        const lastOpeningBrace = lineText.lastIndexOf('{{');
        const lastClosingBrace = lineText.lastIndexOf('}}');
        return lastOpeningBrace > lastClosingBrace;
    }

    function variableCompletion(context) {
        const { state } = context;
        const { from } = state.selection.main;
        if (!isInBraces(state)) return null;
        const word = context.matchBefore(/[\w.]+/);
        let options = flattenObject(props.completionContext)
            .filter(option => !word || option.startsWith(word.text))
            .sort((a, b) => {
                const diff = a.length - b.length;
                if (diff !== 0) return diff;

                return a.localeCompare(b);
            });
        options = options.map((option, i) => {
            return {
                label: option,
                apply: option,
                boost: options.length - i,
            };
        });
        setAutocompletionOpen(true);
        return {
            from: word ? word.from : from,
            options,
        };
    }

    return (
        <CodeMirror
            value={props.currentValue}
            basicSetup={{
                lineNumbers: props.lineNumbers || false,
                foldGutter: props.foldGutter || false,
            }}
            height={props.height}
            data-testid={props.dataTestid}
            readOnly={props.disabled}
            editable={!props.disabled}
            className={`form-control position-relative string-input-cm ${props.disabled && 'cm-disabled'}`}
            extensions={[
                autocompletion({
                    closeOnBlur: true,
                    override: [variableCompletion],
                    tooltipClass: () => 'cm-custom-tooltip',
                    optionClass: () => 'cm-custom-tooltip-option',
                }),
                EditorView.lineWrapping,
                Prec.highest(keymap.of([
                    {
                        key: 'Tab',
                        run(state, dispatch) {
                            if (props.expanded) {
                                return false;
                            }
                            if (autocompletionOpen) {
                                acceptCompletion(state, dispatch);
                                setAutocompletionOpen(false);
                                return true;
                            }
                            if (isInBraces(state.viewState.state)) {
                                return true;
                            }
                            return focusNextTabableElement();
                        },
                    },
                    {
                        key: 'Shift-Tab',
                        run(state, dispatch) {
                            if (props.expanded) {
                                return false;
                            }
                            return focusNextTabableElement(true);
                        },
                    },
                ])),
                // wordHover,
            ]}
            onChange={handleChange}
            onBlur={() => props.onChange(props.currentValue)}
            onUpdate={() => {
                const autocomplete = document.querySelector('.cm-tooltip-autocomplete');
                setAutocompletionOpen(autocomplete !== null);
            }}
        />
    );
};

const StringInput = (props) => {
    const [currentValue, setCurrentValue] = useState(props.value);
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <div className="position-relative">

                <Editor
                    {...props}
                    currentValue={currentValue}
                    setCurrentValue={setCurrentValue}
                />
                {
                    !props.disabled && (
                        <div
                            className="position-absolute string-input-expand"
                            onClick={() => setExpanded(true)}
                        >
                            <BiExpand
                                size={15}
                                color="#6c757d"
                            />
                        </div>
                    )
                }
            </div>
            <Modal
                scrollable
                show={expanded}
                onHide={() => setExpanded(false)}
                dialogClassName="modal-1000w"
            >
                <Modal.Header closeButton />
                <Modal.Body>
                    <Editor
                        {...props}
                        currentValue={currentValue}
                        setCurrentValue={setCurrentValue}
                        height="60vh"
                        foldGutter
                        lineNumbers
                        expanded
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setExpanded(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default StringInput;
